import React from 'react';
import { CalendarMonthOutlined } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { SupervisionMeeting } from '@uplift/api';
import { useCurrentuser, useTimezone } from '@uplift/contexts';
import { useL10n } from '@uplift/hooks';
import { useSupervisionMeetingsQuery } from '@uplift/queries';
import { CalendarDateService } from '@uplift/services';
import { ActivityFeedGroup } from '../../../../components/ActivityFeed';
import { BaseActivityItem } from '../../../../components';


const MeetingCard = ({ meeting, onClick }: { meeting: SupervisionMeeting; onClick: () => void }) => (
  <BaseActivityItem
    icon={CalendarMonthOutlined}
    title="Supervision Meeting"
    color="secondary"
    onClick={onClick}
  >
    <Typography color="text.secondary">
      <Box component="span" sx={{ fontWeight: 500, color: 'text.primary' }}>Duration:</Box>{' '}
      {meeting.durationMinutes} minutes · {meeting.summary}
    </Typography>
  </BaseActivityItem>
);


export type ActivityFeedProps = {
  superviseeId: string;
  onSelectMeeting: (meetingId: string) => void;
};

export const ActivityFeed = ({ superviseeId, onSelectMeeting }: ActivityFeedProps) => {
  const l10n = useL10n();
  const currentUser = useCurrentuser();
  const timezone = useTimezone();
  const now = React.useMemo(() => CalendarDateService.date().tz(timezone), [ timezone ]);

  const { data: meetings = [], isLoading } = useSupervisionMeetingsQuery(
    currentUser.id,
    superviseeId
  );
  const supervisionMeetings = [ ...meetings ].sort((a, b) =>
    CalendarDateService.compare(b.meetingDate, a.meetingDate)
  );

  if (isLoading) {
    return null;
  }

  if (supervisionMeetings.length === 0) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="text.secondary" textAlign="center">
          {l10n.supervisees.noMeetings}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <ActivityFeedGroup
        now={now}
        items={supervisionMeetings}
        getDate={meeting => meeting.meetingDate}
        getKey={meeting => meeting.id}
        renderItem={meeting => (
          <MeetingCard meeting={meeting} onClick={() => onSelectMeeting(meeting.id)} />
        )}
      />
    </Box>
  );
};
